import {injectable, inject} from 'inversify';
import {initializeThemeSelector} from '@openfin/desktop-ui';

import {Inject} from '../common/Injectables';
import {ServiceStore} from '../store/ServiceStore';
import {Action} from '../store/Store';
import {RootState, Immutable} from '../store/State';
import {API} from '../model/API';
import {UserData} from '../model/api/User';
import {UpdateSettings, SetThemeId, UnpinApplication, PinApplication, SettingsState, SettingsData, SetLaunchBarDocked, SettingsInitialState} from '../store/partials/Settings';
import {Authenticated} from '../store/partials/Authentication';

import {AsyncInit} from './AsyncInit';

const updateSettingsClasses = [PinApplication, UnpinApplication, SetLaunchBarDocked, SetThemeId];

@injectable()
export class Settings extends AsyncInit {
    public constructor(
        @inject(Inject.API) private readonly api: API,
        @inject(Inject.STORE) private readonly store: ServiceStore
    ) {
        super();
        this.store.onAction.add(this.onAction, this);
    }

    protected async init() {
        await this.store.initialized;
    }

    private async onAction(action: Action<RootState>): Promise<void> {
        if (action instanceof Authenticated) {
            if (action.loggedIn) {
                let settings = await this.fetchSettings();

                if (settings.version < 1 || settings.version === undefined) {
                    settings = upgradeSettings(settings);
                    await this.writeSettings(settings);
                }

                await this.store.dispatch(new UpdateSettings(settings));

                await initializeThemeSelector({
                    getCurrentThemeId: async () => {
                        return this.store.state.settings.themeId;
                    },
                    setCurrentThemeId: async (id: string) => {
                        return this.store.dispatch(new SetThemeId(id));
                    }
                });
            }
        } else if (updateSettingsClasses.some((updateSettingsClass) => action instanceof updateSettingsClass)) {
            await this.writeSettings(this.store.state.settings);
        }
    }

    private async fetchSettings(): Promise<SettingsData> {
        const settings: SettingsData = await this.api.get(UserData.SETTINGS);
        return {...settings};
    }

    private async writeSettings(settings: Immutable<SettingsState>): Promise<void> {
        await this.api.post(UserData.SETTINGS, {body: {settings}});
    }
}

function upgradeSettings(settings: SettingsData): SettingsData {
    // If we have pre-1 settings, just return the default settings
    return SettingsInitialState;
}
